import {defineStore} from 'pinia'
import Decimal from 'decimal.js'
import {computed, ref} from 'vue'
import type {
  AggregatedJobStats,
  JobDefinition,
  JobDefinitionStatistics,
  JobStatisticBucket,
} from '@/backend/jobs.types'
import type jobs from '@/backend/jobs'
import type {JobStatisticsData} from '@hantera/portal-app'

/**
 * Generates distinct colors for line chart datasets using HSL color wheel
 */
function generateColors(count: number): string[] {
  const colors: string[] = []
  const saturation = 70
  const lightness = 50

  for (let i = 0; i < count; i++) {
    const hue = (i * 360) / Math.max(count, 1)
    colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`)
  }

  return colors
}

/**
 * Calculates optimal bucket size in minutes based on the date range span.
 * Returns bucket size that keeps chart readable (~50-200 data points).
 */
function calculateBucketMinutes(rangeMs: number): number {
  const hours = rangeMs / (1000 * 60 * 60)

  if (hours <= 2) return 1 // ≤2h: 1-minute buckets (up to 120 points)
  if (hours <= 6) return 5 // ≤6h: 5-minute buckets (up to 72 points)
  if (hours <= 24) return 15 // ≤24h: 15-minute buckets (up to 96 points)
  if (hours <= 72) return 60 // ≤3 days: 1-hour buckets (up to 72 points)
  if (hours <= 168) return 360 // ≤7 days: 6-hour buckets (up to 28 points)
  return 1440 // >7 days: daily buckets
}

/**
 * Rounds a date down to the start of a bucket boundary
 */
function roundToBucket(date: Date, bucketMinutes: number): Date {
  const result = new Date(date)
  result.setSeconds(0, 0)

  if (bucketMinutes >= 1440) {
    // Daily buckets - round to start of day
    result.setHours(0, 0, 0, 0)
  } else if (bucketMinutes >= 60) {
    // Hourly or multi-hour buckets
    const hours = Math.floor(bucketMinutes / 60)
    result.setMinutes(0)
    result.setHours(Math.floor(result.getHours() / hours) * hours)
  } else {
    // Minute-based buckets
    result.setMinutes(Math.floor(result.getMinutes() / bucketMinutes) * bucketMinutes)
  }

  return result
}

/**
 * Aggregates a definition's bucket statistics into a single summary row
 */
function aggregateDefinition(def: JobDefinitionStatistics): AggregatedJobStats {
  const totalScheduled = def.buckets.reduce((sum, s) => sum.add(s.scheduled), new Decimal(0))
  const totalSuccessful = def.buckets.reduce((sum, s) => sum.add(s.successful), new Decimal(0))
  const totalFailed = def.buckets.reduce((sum, s) => sum.add(s.failed), new Decimal(0))
  const totalCompleted = totalSuccessful.add(totalFailed)

  let avgExecution: Decimal | null = null
  let minExecution: Decimal | null = null
  let maxExecution: Decimal | null = null

  const executionBuckets = def.buckets.filter(
    (s) => s.avgExecution !== null && s.successful.add(s.failed).greaterThan(0),
  )
  if (executionBuckets.length > 0) {
    let totalWeight = new Decimal(0)
    let weightedSum = new Decimal(0)

    for (const s of executionBuckets) {
      const weight = s.successful.add(s.failed)
      if (s.avgExecution !== null) {
        weightedSum = weightedSum.add(s.avgExecution.mul(weight))
        totalWeight = totalWeight.add(weight)
      }

      if (s.minExecution !== null) {
        if (minExecution === null || s.minExecution.lessThan(minExecution)) {
          minExecution = s.minExecution
        }
      }

      if (s.maxExecution !== null) {
        if (maxExecution === null || s.maxExecution.greaterThan(maxExecution)) {
          maxExecution = s.maxExecution
        }
      }
    }

    if (totalWeight.greaterThan(0)) {
      avgExecution = weightedSum.div(totalWeight)
    }
  }

  return {
    jobDefinitionId: def.jobDefinitionId,
    totalPending: def.scheduled,
    totalScheduled,
    totalSuccessful,
    totalFailed,
    totalCompleted,
    successRate: totalCompleted.greaterThan(0)
      ? totalSuccessful.div(totalCompleted).mul(100)
      : new Decimal(0),
    avgExecution,
    minExecution,
    maxExecution,
  }
}

export interface JobsDashboardServices {
  jobsService: ReturnType<typeof jobs>
}

export interface DateRange {
  from: Date
  to: Date
}

export const useJobsDashboardStore = (services: JobsDashboardServices) => {
  return defineStore('jobs-dashboard', () => {
    const { jobsService } = services

    // === STATE ===
    const jobDefinitions = ref<JobDefinition[]>([])
    const definitionStats = ref<JobDefinitionStatistics[]>([])
    const isLoading = ref(false)
    const error = ref<string | null>(null)

    const dateRange = ref<DateRange>({
      from: new Date(Date.now() - 24 * 60 * 60 * 1000),
      to: new Date(Date.now() + 24 * 60 * 60 * 1000),
    })

    // === COMPUTED ===

    const statsByDefinition = computed(() => {
      const map = new Map<string, JobDefinitionStatistics>()
      for (const def of definitionStats.value) {
        map.set(def.jobDefinitionId, def)
      }
      return map
    })

    /**
     * Aggregates statistics and merges with all job definitions.
     * Job definitions without activity in the time range will show zeros (but
     * still include any current pending count from the backend).
     */
    const aggregatedStats = computed<AggregatedJobStats[]>(() => {
      const consumed = new Set<string>()
      const results: AggregatedJobStats[] = []

      for (const def of jobDefinitions.value) {
        const stats = statsByDefinition.value.get(def.jobDefinitionId)
        if (stats) {
          results.push(aggregateDefinition(stats))
          consumed.add(def.jobDefinitionId)
        } else {
          results.push({
            jobDefinitionId: def.jobDefinitionId,
            totalPending: new Decimal(0),
            totalScheduled: new Decimal(0),
            totalSuccessful: new Decimal(0),
            totalFailed: new Decimal(0),
            totalCompleted: new Decimal(0),
            successRate: new Decimal(0),
            avgExecution: null,
            minExecution: null,
            maxExecution: null,
          })
        }
      }

      for (const stats of definitionStats.value) {
        if (!consumed.has(stats.jobDefinitionId)) {
          results.push(aggregateDefinition(stats))
        }
      }

      return results.sort((a, b) => a.jobDefinitionId.localeCompare(b.jobDefinitionId))
    })

    /**
     * Optimal bucket size in minutes based on date range
     */
    const bucketMinutes = computed(() => {
      const rangeMs = dateRange.value.to.getTime() - dateRange.value.from.getTime()
      return calculateBucketMinutes(rangeMs)
    })

    /**
     * Generates bucket timestamps at the calculated granularity.
     * This ensures the chart shows a linear timeline with appropriate detail.
     */
    const sortedBucketTimes = computed(() => {
      const times: string[] = []
      const minutes = bucketMinutes.value

      const start = roundToBucket(dateRange.value.from, minutes)
      const end = dateRange.value.to

      const current = new Date(start)
      while (current <= end) {
        times.push(current.toISOString())
        current.setMinutes(current.getMinutes() + minutes)
      }

      return times
    })

    /**
     * Time labels formatted for display based on bucket size
     */
    const timeLabels = computed(() => {
      const minutes = bucketMinutes.value

      return sortedBucketTimes.value.map((iso) => {
        const date = new Date(iso)

        if (minutes >= 1440) {
          return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
        } else if (minutes >= 60) {
          return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        } else {
          return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      })
    })

    /**
     * Raw statistics organized by job definition (1-minute buckets from backend)
     */
    const rawStatisticsByJob = computed(() => {
      const map = new Map<string, Map<string, JobStatisticBucket>>()

      for (const def of definitionStats.value) {
        const bucketMap = new Map<string, JobStatisticBucket>()
        for (const bucket of def.buckets) {
          const bucketKey = new Date(bucket.bucketTime).toISOString()
          bucketMap.set(bucketKey, bucket)
        }
        map.set(def.jobDefinitionId, bucketMap)
      }

      return map
    })

    /**
     * Maps display bucket keys to the 1-minute bucket keys that fall within them
     */
    const bucketMapping = computed(() => {
      const minutes = bucketMinutes.value
      const mapping = new Map<string, string[]>()

      for (const def of definitionStats.value) {
        for (const bucket of def.buckets) {
          const rawTime = new Date(bucket.bucketTime)
          const displayBucket = roundToBucket(rawTime, minutes).toISOString()
          const rawBucket = rawTime.toISOString()

          if (!mapping.has(displayBucket)) {
            mapping.set(displayBucket, [])
          }
          const existing = mapping.get(displayBucket)!
          if (!existing.includes(rawBucket)) {
            existing.push(rawBucket)
          }
        }
      }

      return mapping
    })

    /**
     * Dataset for completed jobs line chart.
     * Aggregates 1-minute backend data into display buckets by summing counts.
     */
    const completedLineChartDatasets = computed(() => {
      const datasets: Array<{
        label: string
        data: number[]
        borderColor: string
        backgroundColor: string
      }> = []

      const colors = generateColors(jobDefinitions.value.length)

      jobDefinitions.value.forEach((def, index) => {
        const jobStats = rawStatisticsByJob.value.get(def.jobDefinitionId)

        const data = sortedBucketTimes.value.map((displayBucketKey) => {
          if (!jobStats) return 0

          const rawBuckets = bucketMapping.value.get(displayBucketKey) || []

          let total = 0
          for (const rawKey of rawBuckets) {
            const stat = jobStats.get(rawKey)
            if (stat) {
              total += stat.successful.add(stat.failed).toNumber()
            }
          }

          return total
        })

        if (data.some((v) => v > 0)) {
          datasets.push({
            label: def.jobDefinitionId,
            data,
            borderColor: colors[index],
            backgroundColor: colors[index],
          })
        }
      })

      return datasets
    })

    /**
     * Dataset for execution time line chart.
     * Aggregates 1-minute backend data into display buckets using weighted average.
     */
    const executionTimeLineChartDatasets = computed(() => {
      const datasets: Array<{
        label: string
        data: (number | null)[]
        borderColor: string
        backgroundColor: string
      }> = []

      const colors = generateColors(jobDefinitions.value.length)

      jobDefinitions.value.forEach((def, index) => {
        const jobStats = rawStatisticsByJob.value.get(def.jobDefinitionId)

        const data = sortedBucketTimes.value.map((displayBucketKey) => {
          if (!jobStats) return null

          const rawBuckets = bucketMapping.value.get(displayBucketKey) || []

          let totalWeight = new Decimal(0)
          let weightedSum = new Decimal(0)

          for (const rawKey of rawBuckets) {
            const stat = jobStats.get(rawKey)
            if (stat && stat.avgExecution !== null) {
              const weight = stat.successful.add(stat.failed)
              if (weight.greaterThan(0)) {
                weightedSum = weightedSum.add(stat.avgExecution.mul(weight))
                totalWeight = totalWeight.add(weight)
              }
            }
          }

          if (totalWeight.greaterThan(0)) {
            return weightedSum.div(totalWeight).toNumber()
          }

          return null
        })

        if (data.some((v) => v !== null)) {
          datasets.push({
            label: def.jobDefinitionId,
            data,
            borderColor: colors[index],
            backgroundColor: colors[index],
          })
        }
      })

      return datasets
    })

    // === ACTIONS ===

    async function refresh(): Promise<void> {
      isLoading.value = true
      error.value = null

      try {
        const [definitions, stats] = await Promise.all([
          jobsService.getJobDefinitions(),
          jobsService.getStatistics({
            from: dateRange.value.from,
            to: dateRange.value.to,
          }),
        ])

        jobDefinitions.value = definitions
        definitionStats.value = stats.definitions
      } catch (e: any) {
        console.error('Failed to load job dashboard data:', e)
        error.value = e.message || 'Failed to load job dashboard data'
      } finally {
        isLoading.value = false
      }
    }

    function setDateRange(range: DateRange): void {
      if (range.to > new Date()) {
        dateRange.value = {
          from: dateRange.value.from,
          to: new Date()
        }
      }
      dateRange.value = range
      if (dateRange.value.from > dateRange.value.to) {
        dateRange.value = {
          from: dateRange.value.to,
          to: dateRange.value.from,
        }
      }
    }

    function handleStatisticsEvent(data: JobStatisticsData): void {
      const bucketTime = new Date(data.bucketTimestamp)

      if (bucketTime < dateRange.value.from || bucketTime > dateRange.value.to) return

      const newBucket: JobStatisticBucket = {
        bucketTime,
        scheduled: new Decimal(data.scheduled),
        successful: new Decimal(data.successful),
        failed: new Decimal(data.failed),
        minExecution: data.minExecutionMs !== null ? new Decimal(data.minExecutionMs) : null,
        maxExecution: data.maxExecutionMs !== null ? new Decimal(data.maxExecutionMs) : null,
        avgExecution: data.avgExecutionMs !== null ? new Decimal(data.avgExecutionMs) : null,
        p95Execution: data.p95ExecutionMs !== null ? new Decimal(data.p95ExecutionMs) : null,
        cacheQueueDepth: null,
        totalQueueDepth: null,
      }

      const bucketKey = bucketTime.toISOString()
      let definition = definitionStats.value.find(
        (d) => d.jobDefinitionId === data.jobDefinitionId,
      )

      if (!definition) {
        definition = {
          jobDefinitionId: data.jobDefinitionId,
          scheduled: new Decimal(0),
          due: new Decimal(0),
          buckets: [],
        }
        definitionStats.value.push(definition)
      }

      const existingIndex = definition.buckets.findIndex(
        (b) => new Date(b.bucketTime).toISOString() === bucketKey,
      )

      if (existingIndex >= 0) {
        definition.buckets[existingIndex] = newBucket
      } else {
        definition.buckets.push(newBucket)
      }
    }

    return {
      // State
      jobDefinitions,
      definitionStats,
      isLoading,
      error,
      dateRange,

      // Computed
      aggregatedStats,
      bucketMinutes,
      sortedBucketTimes,
      timeLabels,
      completedLineChartDatasets,
      executionTimeLineChartDatasets,

      // Actions
      refresh,
      setDateRange,
      handleStatisticsEvent,
    }
  })()
}

export type JobsDashboardStore = ReturnType<typeof useJobsDashboardStore>
